import { useContext, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    ClickAwayListener,
    Dialog,
    FormControl,
    FormLabel,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Typography
} from '@mui/material';
import { Close, RadioButtonUnchecked, RadioButtonChecked } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import { DatePicker } from 'formik-mui-lab';
import { LocalizationProvider } from '@mui/lab';
import { Select, TextField } from 'formik-mui';
import * as Yup from 'yup';
import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'helpers/dayjs';
import AdapterDayjs from '@mui/lab/AdapterDayjs';
// Our Components
import { UserContext } from 'context/users/userContext';
import { USERS_ACTIONS } from 'context/users/userReducer';
import UserStatus from './table/UserTableRow/UserStatus';
import KYCStatus from './table/UserTableRow/KYCStatus';
// Services
import { Users } from 'lib/api';
// Utils and Helpers
import { isAdult } from 'helpers';
import {
    USER_ROLES,
    US_PHONE_NUMBER_REGEX,
    FIRST_LAST_NAME_REGEX,
    EMAIL_REGEX,
    US_SSN_REGEX,
    KYC_STATUS
} from 'helpers/constants';
// Assets
import { ReactComponent as GroupIcon } from 'assets/userForm/Group.svg';
import { ReactComponent as SettingsIcon } from 'assets/userForm/Settings.svg';
import './UserForm.css';

export const UserForm = () => {
    const {
        state: { isUserFormModalOpen, user, previousUser },
        dispatch
    } = useContext(UserContext);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedOption, setSelectedOption] = useState('');
    const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);

    const doesUserRequireKYC = () => {
        return !user.id || (user.id && [KYC_STATUS.NOT_SUBMITTED, KYC_STATUS.NOT_SUBMITTED, null].includes(user.kyc_status));
    };
    const initialValues = {
        id: user?.id ?? '',
        firstName: user?.first_name ?? '',
        lastName: user?.last_name ?? '',
        jobTitle: user?.job_title ?? '',
        department: user?.department ?? '',
        ...(doesUserRequireKYC() && {
            dateOfBirth: '',
            idType: '',
            idNumber: ''
        }),
        email: user?.email ?? '',
        phoneNumber: user?.phone_number?.substring(2) ?? '',
        id_role: user?.id_role ?? '',
        kycStatus: user.kyc_status ?? null
    };
    const USER_VALUES_TO_UPDATE = {
        firstName: 'firstName',
        lastName: 'lastName',
        jobTitle: 'jobTitle',
        department: 'department',
        email: 'email',
        phoneNumber: 'phoneNumber',
        ...(doesUserRequireKYC() && {
            dateOfBirth: 'dateOfBirth',
            idType: 'idType',
            idNumber: 'idNumber'
        }),
        id_role: 'id_role'
    };
    const userSchema = Yup.object().shape({
        firstName: Yup.string()
            .matches(FIRST_LAST_NAME_REGEX, { message: 'No special characters allowed' })
            .max(255, 'Max. 255 characters')
            .required('Required'),
        lastName: Yup.string()
            .matches(FIRST_LAST_NAME_REGEX, { message: 'No special characters allowed' })
            .max(255, 'Max. 255 characters')
            .required('Required'),
        jobTitle: Yup.string().max(255, 'Max. 255 characters').optional(),
        department: Yup.string().max(255, 'Max. 255 characters').optional(),
        email: Yup.string().matches(EMAIL_REGEX, { message: 'Please add a valid business email' }).required('Required'),
        phoneNumber: Yup.string().matches(US_PHONE_NUMBER_REGEX, { message: 'Invalid phone number' }).required('Required'),
        ...(doesUserRequireKYC() && {
            idType: Yup.string().required('Required'),
            idNumber: Yup.string()
                .required('Required')
                .when('idType', {
                    is: 'ssn',
                    then: Yup.string().matches(US_SSN_REGEX, { message: 'Invalid SSN number.' }),
                    otherwise: Yup.string().trim().length(9, 'ID number must have 9 characters')
                }),
            dateOfBirth: Yup.date()
                .typeError('Invalid date')
                .test('dateOfBirth', 'The user must be at least 18 years old', dateOfBirth => {
                    return isAdult(dateOfBirth);
                })
                .required()
        })
    });

    const handleUserModalClose = () => {
        setErrorMessage(null);
        dispatch({ type: USERS_ACTIONS.HIDE_USER_FORM_MODAL });
    };

    const handleSubmit = async (values, actions) => {
        try {
            user?.id ? await updateUser(values) : await createUser(values);
            actions.resetForm();
            setErrorMessage(null);
            dispatch({ type: USERS_ACTIONS.RELOAD_USERS });
            dispatch({ type: USERS_ACTIONS.HIDE_USER_FORM_MODAL });
        } catch (error) {
            if (error.response) {
                const { data } = error.response;
                if (data.type === 'Users/PhoneNumberExists') {
                    return actions.setFieldError('phoneNumber', data.message);
                }
                if (data.type === 'Users/EmailExists') {
                    return actions.setFieldError('email', data.message);
                }
                if (data.type === 'Users/ProviderError') {
                    return setErrorMessage(data.message);
                }
            } else {
                setErrorMessage('An unexpected error has occurred. Please try again later.');
            }
        }
    };
    const createUser = async values => {
        const currentFormValues = cloneDeep(values);
        const body = mapValuesToBodyModel(currentFormValues);
        await Users.createUser(body);
    };
    const updateUser = async values => {
        const body = mapValuesToBodyModel(values);
        await Users.updateUser(user?.id, body);
    };
    const mapValuesToBodyModel = values => {
        const currentFormValues = cloneDeep(values);
        const body = {};
        for (const key in currentFormValues) {
            if ((key in USER_VALUES_TO_UPDATE && previousUser[key] !== currentFormValues[key]) || key === 'id_role') {
                if (key === USER_VALUES_TO_UPDATE.dateOfBirth) {
                    body[key] = currentFormValues.dateOfBirth = dayjs(currentFormValues.dateOfBirth).format('YYYY-MM-DD');
                }
                if (key === USER_VALUES_TO_UPDATE.phoneNumber) {
                    body[key] = `+1${currentFormValues.phoneNumber}`;
                }
                if (key === USER_VALUES_TO_UPDATE.idNumber) {
                    body[key] = currentFormValues.idNumber.toString();
                } else {
                    body[key] = currentFormValues[key];
                }
            }
        }
        return body;
    };

    return (
        <Dialog open={isUserFormModalOpen} onClose={() => {}} maxWidth="md">
            <Box className="user-form">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap="10px">
                        <Typography className="user-form__title">{user?.id ? 'Edit User' : 'Add User'}</Typography>
                        {user?.id && (
                            <>
                                <UserStatus user={user} />
                                <KYCStatus user={user} />
                            </>
                        )}
                    </Box>
                    <Box>
                        <IconButton onClick={handleUserModalClose}>
                            <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                        </IconButton>
                    </Box>
                </Box>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize={true}
                    validationSchema={userSchema}
                    onSubmit={handleSubmit}>
                    {({ isSubmitting, setFieldValue, values }) => (
                        <Form>
                            <Grid container rowSpacing={2} columnSpacing={2} sx={{ marginTop: '8px' }}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel required className="user-form__form-label">
                                            First Name
                                        </FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="firstName"
                                            type="text"
                                            size="small"
                                            placeholder="Enter name"
                                            disabled={values.kycStatus === KYC_STATUS.APPROVED}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel required className="user-form__form-label">
                                            Last Name
                                        </FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="lastName"
                                            type="text"
                                            size="small"
                                            placeholder="Enter last name"
                                            disabled={values.kycStatus === KYC_STATUS.APPROVED}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel className="user-form__form-label">Job Title</FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="jobTitle"
                                            type="text"
                                            size="small"
                                            placeholder="Enter job title"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <FormLabel className="user-form__form-label">Department</FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="department"
                                            type="text"
                                            size="small"
                                            placeholder="Enter department"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <ClickAwayListener onClickAway={() => setIsDatepickerOpen(false)}>
                                        <FormControl fullWidth required>
                                            <FormLabel className="user-form__form-label">Date of Birth</FormLabel>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <Field
                                                    component={DatePicker}
                                                    variant="outlined"
                                                    name="dateOfBirth"
                                                    mask="__/__/____"
                                                    open={isDatepickerOpen}
                                                    onAccept={() => setIsDatepickerOpen(false)}
                                                    textField={{
                                                        size: 'small',
                                                        onClick: () => setIsDatepickerOpen(isDatepickerOpen => !isDatepickerOpen)
                                                    }}
                                                    disabled={!doesUserRequireKYC()}
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </ClickAwayListener>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormLabel className="user-form__form-label">ID Number*</FormLabel>
                                    <Grid container columnSpacing={1}>
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth required>
                                                <Field
                                                    component={Select}
                                                    name="idType"
                                                    size="small"
                                                    disabled={!doesUserRequireKYC()}
                                                    onChange={event => {
                                                        setSelectedOption(event.target.value);
                                                        if (event.target.value === 'ssn') {
                                                            setFieldValue('idNumber', '');
                                                        }
                                                    }}>
                                                    <MenuItem value="passport">Passport</MenuItem>
                                                    <MenuItem value="ssn">SSN</MenuItem>
                                                </Field>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={8}>
                                            <FormControl fullWidth>
                                                <Field
                                                    component={TextField}
                                                    variant="outlined"
                                                    name="idNumber"
                                                    type={selectedOption === 'ssn' ? 'password' : 'number'}
                                                    size="small"
                                                    placeholder="Enter number"
                                                    disabled={!doesUserRequireKYC()}
                                                    onChange={event => {
                                                        if (event.target.value.length <= 9) {
                                                            setFieldValue('idNumber', event.target.value);
                                                        }
                                                    }}
                                                    inputProps={{
                                                        maxLength:
                                                            selectedOption === 'ssn' || selectedOption === 'passport'
                                                                ? 9
                                                                : undefined,
                                                        autoComplete: 'new-password'
                                                    }}
                                                    onKeyPress={e => {
                                                        if (
                                                            (selectedOption === 'ssn' || selectedOption === 'passport') &&
                                                            !/[0-9]/.test(e.key)
                                                        ) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required>
                                        <FormLabel className="user-form__form-label">Email Address</FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="email"
                                            type="text"
                                            size="small"
                                            placeholder="Enter email Address"
                                            disabled={values?.id ? true : false}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required>
                                        <FormLabel className="user-form__form-label">Phone Number</FormLabel>
                                        <Field
                                            component={TextField}
                                            variant="outlined"
                                            name="phoneNumber"
                                            type="text"
                                            size="small"
                                            placeholder="Enter phone number"
                                            disabled={values.id && values.phoneNumber ? true : false}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Typography>+1</Typography>
                                                    </InputAdornment>
                                                ),
                                                inputProps: { maxLength: 10 }
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Box marginTop="16px">
                                <FormLabel required className="user-form__form-label">
                                    Select role
                                </FormLabel>
                                <Box
                                    className={`user-form__role ${
                                        values.id_role === USER_ROLES.ADMIN && 'user-form__role-checked'
                                    }`}
                                    onClick={() => setFieldValue('id_role', USER_ROLES.ADMIN)}>
                                    <Box display="flex">
                                        {values.id_role === USER_ROLES.ADMIN ? (
                                            <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                                        )}
                                        <Box>
                                            <Typography component="div" className="user-form__role-name">
                                                Administrator
                                            </Typography>
                                            <Typography component="div" className="user-form__role-description">
                                                Can create and edit cards
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box alignSelf="center">
                                        <SettingsIcon />
                                    </Box>
                                </Box>
                                <Box
                                    className={`user-form__role ${
                                        values.id_role === USER_ROLES.CARD_HOLDER && 'user-form__role-checked'
                                    }`}
                                    onClick={() => setFieldValue('id_role', USER_ROLES.CARD_HOLDER)}>
                                    <Box display="flex">
                                        {values.id_role === USER_ROLES.CARD_HOLDER ? (
                                            <RadioButtonChecked sx={{ color: '#1BABE2', marginRight: '12px' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ color: '#909DA6', marginRight: '12px' }} />
                                        )}
                                        <Box>
                                            <Typography component="div" className="user-form__role-name">
                                                Cardholder
                                            </Typography>
                                            <Typography component="div" className="user-form__role-description">
                                                Can see and use cards in their name
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box alignSelf="center">
                                        <GroupIcon />
                                    </Box>
                                </Box>
                            </Box>
                            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                            <Box display="flex" justifyContent="end" sx={{ marginTop: '32px' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleUserModalClose}
                                    disabled={isSubmitting}
                                    sx={{ marginRight: '16px' }}>
                                    Cancel
                                </Button>
                                <Button variant="contained" type="submit" disabled={isSubmitting}>
                                    {isSubmitting ? <CircularProgress size="20px" /> : user?.id ? 'Update' : 'Create'}
                                </Button>
                            </Box>
                        </Form>
                    )}
                </Formik>
            </Box>
        </Dialog>
    );
};

export default UserForm;
